import React, { useCallback, useEffect, useState } from 'react';
import { adminAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { parseServerDate } from '../../utils/serverDate';
import AdminCredits from '../../components/AdminCredits';
import ConfirmDialog from '../../components/ConfirmDialog';
import './Admin.css';

const LIMIT = 25;
const PLANS = ['starter', 'growth', 'pro'];
const STATUSES = ['active', 'canceled', 'none'];
const STATUS_LABELS = { active: 'Active', canceled: 'Cancelled', none: 'No subscription' };
const INVITE_PLANS = [['starter', 'Starter'], ['growth', 'Growth'], ['pro', 'Pro']];
// Row highlight colours (the server accepts exactly these)
const COLORS = [
  ['green', 'Green'], ['blue', 'Blue'], ['amber', 'Amber'],
  ['red', 'Red'], ['purple', 'Purple'], ['grey', 'Grey'],
];

const dateText = (value) => {
  const d = parseServerDate(value);
  return d ? d.toLocaleDateString() : '—';
};

const planText = (c) => {
  const plan = c.subscription?.plan;
  return plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : '—';
};

function StatusBadge({ customer }) {
  if (customer.disabled) return <span className="admin-pill admin-pill-red">Disabled</span>;
  const status = customer.subscription?.status || 'none';
  const tone = status === 'active' ? 'green' : status === 'none' ? 'grey' : 'amber';
  return <span className={`admin-pill admin-pill-${tone}`}>{STATUS_LABELS[status] || status}</span>;
}

function AdminCustomers() {
  const { startViewAs } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [plan, setPlan] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [confirm, setConfirm] = useState(null);   // { customer, kind }
  const [creditsFor, setCreditsFor] = useState(null);
  const [manage, setManage] = useState(null);     // customer open in the Manage modal
  const [noteForm, setNoteForm] = useState({ color: '', note: '' });
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState('');
  const [invite, setInvite] = useState(null);   // validated invite awaiting confirmation
  const [inviteForm, setInviteForm] = useState({ email: '', plan_name: 'starter', billing_cycle: 'monthly', order_id: '' });
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteError, setInviteError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: LIMIT };
      if (search) params.search = search;
      if (plan) params.plan = plan;
      if (status) params.status = status;
      const res = await adminAPI.getCustomers(params);
      setCustomers(res.data?.customers || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load customers');
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, plan, status]);

  useEffect(() => { load(); }, [load]);

  const submitSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const openManage = (customer) => {
    setManage(customer);
    setNoteForm({ color: customer.admin_color || '', note: customer.admin_note || '' });
    setNoteError('');
  };

  const saveNote = async () => {
    if (!manage) return;
    setNoteSaving(true);
    setNoteError('');
    try {
      const res = await adminAPI.setCustomerNote(manage.id, { color: noteForm.color, note: noteForm.note });
      const color = res.data?.admin_color || null;
      const note = res.data?.admin_note || null;
      // Update the row in place so the table reflects it without a reload
      setCustomers((list) => list.map((c) => (c.id === manage.id ? { ...c, admin_color: color, admin_note: note } : c)));
      setManage((m) => (m ? { ...m, admin_color: color, admin_note: note } : m));
      setMessage(`Saved the highlight and note for ${manage.email}.`);
      setManage(null);
    } catch (err) {
      setNoteError(err?.response?.data?.error || err.message || 'Could not save');
    } finally {
      setNoteSaving(false);
    }
  };

  const startAction = (kind) => {
    const customer = manage;
    setManage(null);
    if (kind === 'credits') {
      setCreditsFor(customer);
    } else {
      setConfirm({ customer, kind });
    }
  };

  const runAction = async () => {
    if (!confirm) return;
    const { customer, kind } = confirm;
    setError('');
    setMessage('');
    try {
      if (kind === 'view') {
        await startViewAs(customer.id);   // full page load into the customer app
        return;
      }
      if (kind === 'reset') {
        await adminAPI.sendCustomerReset(customer.id);
        setMessage(`Password reset email sent to ${customer.email}.`);
      } else if (kind === 'disable') {
        await adminAPI.disableCustomer(customer.id);
        setMessage(`${customer.email} disabled.`);
        load();
      } else if (kind === 'enable') {
        await adminAPI.enableCustomer(customer.id);
        setMessage(`${customer.email} enabled.`);
        load();
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Action failed');
    }
  };

  const submitInvite = (e) => {
    e.preventDefault();
    setInviteError('');
    setInviteMessage('');
    const email = inviteForm.email.trim();
    const orderId = inviteForm.order_id.trim();
    if (!email) { setInviteError('Enter an email address.'); return; }
    if (orderId && !/^\d+$/.test(orderId)) { setInviteError('Order id must be numeric.'); return; }
    setInvite({ ...inviteForm, email, order_id: orderId });
  };

  const sendInvite = async () => {
    if (!invite) return;
    const payload = { email: invite.email, plan_name: invite.plan_name, billing_cycle: invite.billing_cycle };
    if (invite.order_id) payload.order_id = invite.order_id;
    setInvite(null);
    try {
      const res = await adminAPI.resendSignupInvite(payload);
      if (res?.data?.success === false) {
        // 207: the invite was created but the email could not be sent
        setInviteError(`The email to ${payload.email} was NOT sent (check the email settings on the backend). Share this activation link manually: ${res.data.signup_url}`);
        return;
      }
      setInviteMessage(`Signup invite sent to ${payload.email} (${payload.plan_name}, ${payload.billing_cycle}).`);
      setInviteForm({ ...inviteForm, email: '', order_id: '' });
    } catch (err) {
      setInviteError(err?.response?.data?.error || err.message || 'Could not send the invite');
    }
  };

  const confirmCopy = (c) => {
    if (!c) return {};
    const { customer, kind } = c;
    if (kind === 'view') return {
      title: 'View as customer',
      message: `Open the app as ${customer.email} in read-only mode? Nothing can be changed while viewing, and the view is audited and expires after an hour.`,
      confirmText: 'View as customer', variant: 'primary',
    };
    if (kind === 'reset') return {
      title: 'Send reset email',
      message: `Email a password reset link to ${customer.email}?`,
      confirmText: 'Send email', variant: 'primary',
    };
    if (kind === 'disable') return {
      title: 'Disable account',
      message: `Disable ${customer.email}? They will be signed out and unable to log in until you enable the account again.`,
      confirmText: 'Disable', variant: 'danger',
    };
    return {
      title: 'Enable account',
      message: `Enable ${customer.email}?`,
      confirmText: 'Enable', variant: 'primary',
    };
  };

  const pages = Math.max(1, Math.ceil(total / LIMIT));
  const copy = confirmCopy(confirm);

  return (
    <div className="admin-page">
      <div className="admin-page-head">
        <h1>Customers</h1>
        <p className="admin-subtitle">{total.toLocaleString()} {total === 1 ? 'customer' : 'customers'}</p>
      </div>

      <form className="admin-controls admin-filters" onSubmit={submitSearch}>
        <input
          className="form-input"
          style={{ width: 260 }}
          placeholder="Search email or name"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <select className="form-select admin-select" value={plan} onChange={(e) => { setPlan(e.target.value); setPage(1); }}>
          <option value="">All plans</option>
          {PLANS.map((p) => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
        </select>
        <select className="form-select admin-select" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
        <button type="submit" className="btn btn-primary" disabled={loading}>Search</button>
      </form>

      {error && <div className="admin-error">{error}</div>}
      {message && <div className="admin-notice">{message}</div>}

      <div className="admin-table-wrapper">
        <table className="admin-table admin-customers-table">
          <thead>
            <tr>
              <th>Customer</th><th>Plan</th><th>Status</th><th>Credits</th><th>Last login</th><th />
            </tr>
          </thead>
          <tbody>
            {customers.map((c) => (
              <tr key={c.id} className={c.admin_color ? `row-${c.admin_color}` : undefined}>
                <td>
                  <div className="admin-customer-email">{c.email}</div>
                  <div className="admin-customer-sub">
                    {c.name || 'No name'}
                    {c.admin_note && <span className="admin-note-chip" title={c.admin_note}>{c.admin_note}</span>}
                  </div>
                </td>
                <td>
                  {planText(c)}
                  {c.subscription?.billing_cycle && (
                    <span className="admin-cycle"> · {c.subscription.billing_cycle}</span>
                  )}
                </td>
                <td><StatusBadge customer={c} /></td>
                <td>
                  <strong>{(c.credits?.total ?? 0).toLocaleString()}</strong>
                  <span className="admin-cycle"> ({(c.credits?.monthly ?? 0).toLocaleString()} + {(c.credits?.topup ?? 0).toLocaleString()})</span>
                </td>
                <td>{dateText(c.last_login)}</td>
                <td className="admin-row-action">
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => openManage(c)}>Manage</button>
                </td>
              </tr>
            ))}
            {customers.length === 0 && !loading && (
              <tr><td colSpan={6} className="admin-empty">No customers found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="admin-pagination">
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>Prev</button>
        <span>Page {page} of {pages}</span>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setPage((p) => p + 1)} disabled={page >= pages || loading}>Next</button>
      </div>

      <div className="card admin-card" style={{ marginTop: 24 }}>
        <h2 className="card-title">Send signup invite</h2>
        <p className="admin-hint">
          Creates the account link and emails it straight away. Use it for a customer who paid but never got the
          activation email, or to give someone an account without a ThriveCart payment (pick Yearly so credits refill monthly).
          Not for existing accounts.
        </p>
        <form className="admin-controls" onSubmit={submitInvite} style={{ flexWrap: 'wrap' }}>
          <input
            className="form-input"
            type="email"
            style={{ width: 240 }}
            placeholder="Customer email"
            value={inviteForm.email}
            onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
          />
          <select className="form-select admin-select" value={inviteForm.plan_name} onChange={(e) => setInviteForm({ ...inviteForm, plan_name: e.target.value })}>
            {INVITE_PLANS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <select className="form-select admin-select" value={inviteForm.billing_cycle} onChange={(e) => setInviteForm({ ...inviteForm, billing_cycle: e.target.value })}>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
          <input
            className="form-input"
            style={{ width: 160 }}
            placeholder="Order id (optional)"
            value={inviteForm.order_id}
            onChange={(e) => setInviteForm({ ...inviteForm, order_id: e.target.value })}
          />
          <button type="submit" className="btn btn-primary">Send signup invite</button>
        </form>
        {inviteError && <div className="admin-error" style={{ marginTop: 8 }}>{inviteError}</div>}
        {inviteMessage && <div className="admin-notice" style={{ marginTop: 8 }}>{inviteMessage}</div>}
      </div>

      {manage && (
        <div className="admin-modal-backdrop" onClick={() => setManage(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflow: 'auto' }}>
            <div className="admin-modal-head">
              <div>
                <h2 className="admin-modal-title">{manage.email}</h2>
                <p className="admin-subtitle">{manage.name || 'No name'}</p>
              </div>
              <StatusBadge customer={manage} />
            </div>

            <dl className="admin-details">
              <div><dt>Plan</dt><dd>{planText(manage)}{manage.subscription?.billing_cycle ? ` (${manage.subscription.billing_cycle})` : ''}</dd></div>
              {manage.subscription?.cancel_at_period_end && (
                <div><dt>Cancelling</dt><dd>Ends {dateText(manage.subscription.current_period_end)}. Still cancel it in ThriveCart.</dd></div>
              )}
              {manage.subscription?.cancellation_reason && (
                <div><dt>Cancel reason</dt><dd>{manage.subscription.cancellation_reason}</dd></div>
              )}
              <div><dt>Credits</dt><dd>{(manage.credits?.monthly ?? 0).toLocaleString()} monthly + {(manage.credits?.topup ?? 0).toLocaleString()} top-up</dd></div>
              <div><dt>Created</dt><dd>{dateText(manage.created_at)}</dd></div>
              <div><dt>Last login</dt><dd>{dateText(manage.last_login)}</dd></div>
            </dl>

            <h3 className="admin-section-title">Highlight and note</h3>
            <p className="admin-hint">Only you see this. Use a colour to mark a row, for example green for a free account.</p>
            <div className="admin-swatches" role="radiogroup" aria-label="Row colour">
              <button
                type="button"
                className={`admin-swatch admin-swatch-none ${noteForm.color === '' ? 'selected' : ''}`}
                onClick={() => setNoteForm({ ...noteForm, color: '' })}
                aria-pressed={noteForm.color === ''}
              >
                None
              </button>
              {COLORS.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`admin-swatch swatch-${value} ${noteForm.color === value ? 'selected' : ''}`}
                  onClick={() => setNoteForm({ ...noteForm, color: value })}
                  aria-pressed={noteForm.color === value}
                  title={label}
                  aria-label={label}
                />
              ))}
            </div>
            <textarea
              className="form-textarea admin-note-input"
              rows={3}
              maxLength={500}
              placeholder="Note (for example: free account, friend of the business)"
              value={noteForm.note}
              onChange={(e) => setNoteForm({ ...noteForm, note: e.target.value })}
            />
            {noteError && <div className="admin-error" style={{ marginTop: 8 }}>{noteError}</div>}
            <div className="admin-modal-actions">
              <button type="button" className="btn btn-primary" onClick={saveNote} disabled={noteSaving}>
                {noteSaving ? 'Saving…' : 'Save highlight and note'}
              </button>
            </div>

            <h3 className="admin-section-title">Actions</h3>
            <div className="admin-action-grid">
              <button type="button" className="btn btn-secondary" onClick={() => startAction('view')} disabled={manage.disabled}>View as customer</button>
              <button type="button" className="btn btn-secondary" onClick={() => startAction('credits')}>Credits</button>
              <button type="button" className="btn btn-secondary" onClick={() => startAction('reset')} disabled={manage.disabled}>Send reset email</button>
              {manage.disabled ? (
                <button type="button" className="btn btn-success" onClick={() => startAction('enable')}>Enable account</button>
              ) : (
                <button type="button" className="btn btn-danger" onClick={() => startAction('disable')}>Disable account</button>
              )}
            </div>

            <div className="admin-modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setManage(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={runAction}
        title={copy.title}
        message={copy.message}
        confirmText={copy.confirmText}
        variant={copy.variant}
      />

      <ConfirmDialog
        isOpen={!!invite}
        onClose={() => setInvite(null)}
        onConfirm={sendInvite}
        title="Send signup invite"
        message={invite ? `Send ${invite.email} a signup invite for the ${invite.plan_name} plan (${invite.billing_cycle})${invite.order_id ? `, order ${invite.order_id}` : ''}?` : ''}
        confirmText="Send invite"
        variant="primary"
      />

      {creditsFor && (
        <div className="admin-modal-backdrop" onClick={() => { setCreditsFor(null); load(); }}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflow: 'auto' }}>
            <AdminCredits initialEmail={creditsFor.email} />
            <div className="admin-modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => { setCreditsFor(null); load(); }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminCustomers;
